/* =======================================================================
   assets/price-fetch.js.liquid
   – Dynamic sweepstakes entries + variant‑aware pricing
   ======================================================================= */

/* ---------- helpers --------------------------------------------------- */
function sanitizeId(str) {
    return String(str).replace(/\D/g, '');
  }
  
  function formatSubtotal(cents) {
    const s = String(cents);
    return s.length > 2 ? parseFloat(`${s.slice(0, -2)}.${s.slice(-2)}`) : cents / 100;
  }
  
  /* ---------- core GraphQL fetch & DOM update --------------------------- */
  function runSweepstakesFetch(cardEl) {
    if (!cardEl?.isConnected) return; // node got removed; guard
  
    const productId    = `gid://shopify/Product/${cardEl.dataset.productId}`;
    const platformId   = `gid://shopify/Shop/${cardEl.dataset.shopGid}`;
    const subtotal     = parseFloat(cardEl.dataset.subtotal);
    const sweepstakeId = sanitizeId(cardEl.dataset.sweepstakeId) || 'default';
  
    const query = `
      query QuerySweepstakeDetailsByProductId(
        $platform: String!
        $platformId: String!
        $productIds: [String!]
      ) {
        sweepstakeDetailsByProductId(
          platform: $platform
          platformId: $platformId
          productIds: $productIds
        ) {
          id
          entryManagement
          productsAllowedByDefault
          bonusEntriesDefault
          scheduledBonusValue
          scheduledBonusActive
          isActive
          products(productIds: $productIds) {
            productId
            productAllowed
            productBonusEntries
            productMultiplier
          }
        }
      }
    `;
  
    const variables = {
      platform:   'Shopify',
      platformId: platformId,
      productIds: [productId],
    };
  
    (async () => {
      try {
        const res = await fetch('https://backend.cleansweeps.app/graphql', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify({ query, variables }),
        });
        if (!res.ok) throw new Error(`Network error: ${res.status}`);
  
        const json = await res.json();
        if (json.errors?.length) {
          console.error('GraphQL errors:', json.errors);
          return;
        }
  
        const details = json.data?.sweepstakeDetailsByProductId;
        if (!details?.length) return;
  
        const matched =
          details.find(d => sanitizeId(d.id) === sweepstakeId) ||
          details.find(d => d.isActive) ||
          details[0];
        if (!matched) return;
  
        /* ----- calculations -------------------------------------------- */
        const entryMgmt   = JSON.parse(matched.entryManagement);
        const dollarSpent = parseFloat(entryMgmt.dollarSpent);
        const baseEntriesPer = parseFloat(entryMgmt.entryPer);
        const scheduledBonusActive = matched?.scheduledBonusActive === true;
        const scheduledBonusValueNum = Number(matched?.scheduledBonusValue);
        const entriesPer =
          scheduledBonusActive && Number.isFinite(scheduledBonusValueNum) && scheduledBonusValueNum > 0
            ? scheduledBonusValueNum
            : baseEntriesPer;
        const roundUp     = entryMgmt.roundUp ?? true;
        const roundFn     = roundUp ? Math.ceil : Math.floor;
  
        let finalMultiplier = entriesPer;
        let finalEntries;
  
        if ((!matched.products?.length) && matched.productsAllowedByDefault) {
          finalEntries =
            roundFn((entriesPer / dollarSpent) * roundFn(subtotal)) +
            matched.bonusEntriesDefault;
        } else {
          const prod = matched.products?.[0];
          if (!prod?.productAllowed) {
            finalEntries = 'Product Not Eligible';
          } else {
            const prodMult = parseFloat(prod.productMultiplier);
            finalEntries =
              roundFn((entriesPer / dollarSpent) * roundFn(subtotal) * prodMult) +
              prod.productBonusEntries;
            /* expedition did not want builtin multipliers shown in the final */
            finalMultiplier = roundFn(entriesPer * prodMult);
          }
        }
  
        /* ----- DOM updates --------------------------------------------- */
        const multiplierBoxes = cardEl.querySelectorAll(
          `#flash-text-dynamic-${cardEl.dataset.productId}`
        );
        if (multiplierBoxes) {
            for ( multiplierBox of multiplierBoxes ) {  
          if (finalMultiplier > 1) {
            multiplierBox.classList.remove('hidden');
            multiplierBox.style.display = 'flex';
            multiplierBox.textContent = `${finalMultiplier}X`;
          } else {
            multiplierBox.classList.add('hidden');
            multiplierBox.style.display = 'none';
            multiplierBox.textContent = '';
          }
        }
        }
  
        const entriesBox = cardEl.querySelector(
          `#entries-text-placeholder-thumb-${cardEl.dataset.productId}-${sweepstakeId}`
        );
        if (!entriesBox) return;
  
        if (typeof finalEntries === 'number') finalEntries = String(finalEntries);
        entriesBox.textContent =
          finalEntries === 'Product Not Eligible'
            ? finalEntries
            : `${finalEntries} Entries`;
      } catch (err) {
        console.error('Sweepstake fetch error:', err);
      }
    })();
  }
  
  /* ---------- variant‑change awareness ---------------------------------- */
  function setupVariantSync() {
    const productJsonEl = document.querySelector(
      '[type="application/json"][data-product]'
    );
    if (!productJsonEl) return;
  
    const productJson   = JSON.parse(productJsonEl.textContent) || {};
    const productIdAttr = String(productJson.id);                          // NEW
  
    const variantData = productJson.variants || [];
    if (!variantData.length) return;
  
    const priceByVariant = Object.fromEntries(
      variantData.map(v => [Number(v.id), parseInt(v.price, 10)])
    );
  
    const variantSelect = document.querySelector('select[name="id"]');
    if (!variantSelect) return;
  
    let prevSubtotal = null;
  
    const refresh = () => {
      const vid   = Number(variantSelect.value);          // ensure numeric key
      const cents = priceByVariant[vid];
      if (!cents) {
        setTimeout(refresh, 60);                          // theme still fetching JSON
        return;
      }
  
      const subtotal = formatSubtotal(cents);
      if (subtotal === prevSubtotal) return;
      prevSubtotal = subtotal;
  
      /* NEW/UPDATED — always rescan the DOM to grab *fresh* price snippets */
      scan();                                             // initialise any new nodes
  
      document
        .querySelectorAll(
          `[data-price-context="card"][data-product-id="${productIdAttr}"]`
        )
        .forEach(card => {
          card.dataset.subtotal = subtotal;
          runSweepstakesFetch(card);
        });
    };
  
    variantSelect.addEventListener('change', refresh);
    window.addEventListener('popstate', refresh); // covers ?variant= pushes
  }
  
  /* ---------- per‑card init -------------------------------------------- */
  function initOneCard(el) {
    if (el.__priceCardInit) return; // idempotent
    el.__priceCardInit = true;
    runSweepstakesFetch(el);
  }
  
  /* ---------- scanning utilities --------------------------------------- */
  function scan(root = document) {
    root
      .querySelectorAll('[data-price-context="card"]')
      .forEach(initOneCard);
  }
  
  /* ---------- bootstrapping -------------------------------------------- */
  document.addEventListener('DOMContentLoaded', () => {
    scan();
    setupVariantSync(); // set up only once on product pages
  });
  document.addEventListener('shopify:section:load', e => scan(e.target));
  
  const observer = new MutationObserver(muts => {
    for (const m of muts) {
      m.addedNodes.forEach(node => {
        if (node.nodeType !== 1) return;
        if (node.matches?.('[data-price-context="card"]')) initOneCard(node);
        if (node.querySelector) scan(node);
      });
    }
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });
  
